import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  BarChart3, Users, BookOpen, Clock, TrendingUp, TrendingDown,
  CheckCircle, XCircle, AlertTriangle, Award, Target, Activity,
  ArrowLeft, RefreshCcw, Download, Filter, ChevronRight,
  GraduationCap, Brain, Layers, FileText, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';

const CourseAnalyticsPage = ({ onMenuClick }) => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [studentProgress, setStudentProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  
  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch course details
      const courseResponse = await api.get(`/content/courses/${courseId}`);
      setCourse(courseResponse.data);
      
      // Fetch analytics
      const analyticsResponse = await api.get(`/content/courses/${courseId}/analytics`);
      setAnalytics(analyticsResponse.data);
      
      // Fetch student progress
      const progressResponse = await api.get(`/content/courses/${courseId}/student-progress`);
      setStudentProgress(progressResponse.data);
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Erreur lors du chargement des analytics');
    } finally {
      setLoading(false);
    }
  }, [courseId]);
  
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);
  
  // Calculate additional metrics
  const getCompletionDistribution = () => {
    if (!studentProgress.length) return { notStarted: 0, inProgress: 0, completed: 0 };
    
    const notStarted = studentProgress.filter(s => s.progress_percent === 0).length;
    const completed = studentProgress.filter(s => s.progress_percent === 100).length;
    const inProgress = studentProgress.length - notStarted - completed;
    
    return { notStarted, inProgress, completed };
  };
  
  const distribution = getCompletionDistribution();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!course || !analytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Données non disponibles</p>
          <Button onClick={() => navigate('/admin/courses')} className="mt-4">
            Retour aux cours
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/admin/courses')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                Analytics: {course.name}
              </h1>
              <p className="text-sm text-slate-500">
                {analytics.total_lessons} leçons • {analytics.students_enrolled} étudiants inscrits
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toute la période</SelectItem>
                <SelectItem value="week">7 derniers jours</SelectItem>
                <SelectItem value="month">30 derniers jours</SelectItem>
                <SelectItem value="quarter">3 derniers mois</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAnalytics}>
              <RefreshCcw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Exporter
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Enrolled Students */}
          <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-violet-100 text-sm">Étudiants inscrits</p>
                  <p className="text-3xl font-bold mt-1">{analytics.students_enrolled}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-violet-100">
                <TrendingUp className="w-4 h-4 mr-1" />
                <span>Actifs ce mois</span>
              </div>
            </CardContent>
          </Card>

          {/* Average Completion */}
          <Card className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">Progression moyenne</p>
                  <p className="text-3xl font-bold mt-1">{analytics.average_completion}%</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Target className="w-6 h-6" />
                </div>
              </div>
              <Progress value={analytics.average_completion} className="mt-4 bg-emerald-400/30" />
            </CardContent>
          </Card>

          {/* Quiz Average */}
          <Card className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Score moyen aux quiz</p>
                  <p className="text-3xl font-bold mt-1">
                    {analytics.quiz_average_score ? `${analytics.quiz_average_score}%` : 'N/A'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <Brain className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-blue-100">
                <Activity className="w-4 h-4 mr-1" />
                <span>{analytics.quiz_attempts} tentatives</span>
              </div>
            </CardContent>
          </Card>

          {/* Students Stuck */}
          <Card className={`bg-gradient-to-br ${analytics.students_stuck > 0 ? 'from-amber-500 to-orange-600' : 'from-slate-500 to-slate-600'} text-white`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">Étudiants en difficulté</p>
                  <p className="text-3xl font-bold mt-1">{analytics.students_stuck}</p>
                </div>
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-sm text-amber-100">
                {analytics.students_stuck > 0 ? (
                  <>
                    <TrendingDown className="w-4 h-4 mr-1" />
                    <span>Progression &lt; 50%</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    <span>Tous en bonne voie</span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Module Stats & Distribution */}
          <div className="lg:col-span-2 space-y-6">
            {/* Completion Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-violet-500" />
                  Distribution de la progression
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="w-12 h-12 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-2">
                      <XCircle className="w-6 h-6 text-slate-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{distribution.notStarted}</p>
                    <p className="text-sm text-slate-500">Non commencé</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <div className="w-12 h-12 bg-amber-200 dark:bg-amber-800 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Activity className="w-6 h-6 text-amber-600" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{distribution.inProgress}</p>
                    <p className="text-sm text-slate-500">En cours</p>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                    <div className="w-12 h-12 bg-emerald-200 dark:bg-emerald-800 rounded-full flex items-center justify-center mx-auto mb-2">
                      <CheckCircle className="w-6 h-6 text-emerald-600" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{distribution.completed}</p>
                    <p className="text-sm text-slate-500">Terminé</p>
                  </div>
                </div>
                
                {/* Progress Bar Visualization */}
                <div className="mt-6">
                  <div className="flex h-4 rounded-full overflow-hidden bg-slate-200">
                    {studentProgress.length > 0 && (
                      <>
                        <div 
                          className="bg-slate-400 transition-all" 
                          style={{ width: `${(distribution.notStarted / studentProgress.length) * 100}%` }}
                        />
                        <div 
                          className="bg-amber-500 transition-all" 
                          style={{ width: `${(distribution.inProgress / studentProgress.length) * 100}%` }}
                        />
                        <div 
                          className="bg-emerald-500 transition-all" 
                          style={{ width: `${(distribution.completed / studentProgress.length) * 100}%` }}
                        />
                      </>
                    )}
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Module Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5 text-violet-500" />
                  Performance par module
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analytics.module_stats?.map((module, index) => (
                    <div key={module.id} className="p-4 border rounded-lg hover:border-violet-200 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-violet-100 text-violet-700 rounded flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </span>
                          <span className="font-medium text-slate-900 dark:text-white">{module.name}</span>
                        </div>
                        <Badge variant="outline">
                          {module.lesson_count} leçons
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="flex-1">
                          <Progress 
                            value={module.lesson_count > 0 ? (module.completions / (module.lesson_count * (analytics.students_enrolled || 1))) * 100 : 0} 
                            className="h-2"
                          />
                        </div>
                        <span className="text-sm text-slate-500 min-w-[60px] text-right">
                          {module.completions} complétions
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  {(!analytics.module_stats || analytics.module_stats.length === 0) && (
                    <div className="text-center py-8 text-slate-500">
                      <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Aucun module dans ce cours</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Student List */}
          <div className="space-y-6">
            {/* Student Progress List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-violet-500" />
                    Progression des étudiants
                  </div>
                  <Badge variant="outline">{studentProgress.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[500px]">
                  <div className="p-4 space-y-3">
                    {studentProgress.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>Aucun étudiant inscrit</p>
                      </div>
                    ) : (
                      studentProgress.map((student) => (
                        <div 
                          key={student.student_id}
                          className="p-3 border rounded-lg hover:border-violet-200 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                                <span className="text-sm font-medium text-violet-700">
                                  {student.name?.charAt(0) || '?'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-sm text-slate-900 dark:text-white">
                                  {student.name}
                                </p>
                                <p className="text-xs text-slate-500">{student.email}</p>
                              </div>
                            </div>
                            <Badge className={
                              student.progress_percent === 100 ? 'bg-emerald-100 text-emerald-700' :
                              student.progress_percent >= 50 ? 'bg-blue-100 text-blue-700' :
                              student.progress_percent > 0 ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'
                            }>
                              {student.progress_percent}%
                            </Badge>
                          </div>
                          <Progress value={student.progress_percent} className="h-1.5" />
                          <div className="flex justify-between text-xs text-slate-500 mt-2">
                            <span>{student.completed_lessons}/{student.total_lessons} leçons</span>
                            {student.average_quiz_score !== null && (
                              <span>Quiz: {student.average_quiz_score}%</span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Actions rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate(`/admin/courses/${courseId}/edit`)}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Modifier le contenu
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => toast.info('Fonctionnalité en cours de développement')}
                >
                  <Award className="w-4 h-4 mr-2" />
                  Voir les certificats
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => toast.info('Fonctionnalité en cours de développement')}
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Voir les quiz
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CourseAnalyticsPage;
